/**
 * TranslationLoader
 * Handles lazy loading of translation files with LRU caching
 * Implements ITranslationLoader interface for dependency injection
 */

import type {
  ITranslationLoader,
  Translations,
  TranslationParams,
  CacheEntry,
} from '../../types/i18n';
import {
  MAX_CACHE_SIZE,
  TOP_20_LANGUAGE_CODES,
  TRANSLATION_PATH_PATTERN,
  DEFAULT_LANGUAGE,
  IS_DEV_MODE,
} from '../../config/i18n.config';

export class TranslationLoader implements ITranslationLoader {
  private cache: Map<string, CacheEntry> = new Map();
  private loadingPromises: Map<string, Promise<Translations>> = new Map();
  private currentTranslations: Translations = {};
  private currentLanguage: string = DEFAULT_LANGUAGE;
  private missingKeys: Set<string> = new Set();
  private isPreloading: boolean = false;

  /**
   * Load translations for a given language
   * Implements LRU caching and lazy loading
   * @param language Language code to load
   * @returns Promise resolving to translations object
   */
  async loadTranslation(language: string): Promise<Translations> {
    try {
      // Check cache first
      const cached = this.getFromCache(language);
      if (cached) {
        // Only update current translations if not preloading
        if (!this.isPreloading) {
          this.currentTranslations = cached;
          this.currentLanguage = language;
        }
        return cached;
      }

      // Check if already loading
      const existingPromise = this.loadingPromises.get(language);
      if (existingPromise) {
        return existingPromise;
      }

      // Start new load
      const loadPromise = this.fetchTranslationFile(language);
      this.loadingPromises.set(language, loadPromise);

      try {
        const translations = await loadPromise;

        // Add to cache
        this.addToCache(language, translations);

        // Update current translations and language ONLY if not preloading
        // During preloading, we load multiple languages but don't want to overwrite the active one
        if (!this.isPreloading) {
          this.currentTranslations = translations;
          this.currentLanguage = language;
        }

        return translations;
      } finally {
        // Clean up loading promise
        this.loadingPromises.delete(language);
      }
    } catch (error) {
      console.error(`[TranslationLoader] Failed to load language: ${language}`, error);

      // Return empty object on error (fallback to keys)
      return {};
    }
  }

  /**
   * Get translation for a specific key with optional parameter interpolation
   * @param key Translation key (supports dot notation)
   * @param params Optional parameters for interpolation
   * @returns Translated string or key as fallback
   */
  getTranslation(key: string, params?: TranslationParams): string {
    try {
      // Debug log removed - was causing excessive console output

      // Get translation from current translations
      const translation = this.getNestedValue(this.currentTranslations, key);

      if (translation === undefined || translation === null) {
        // Try English fallback before giving up (standard i18n behavior)
        if (this.currentLanguage !== DEFAULT_LANGUAGE) {
          const englishTranslations = this.getFromCache(DEFAULT_LANGUAGE);
          if (englishTranslations) {
            const fallback = this.getNestedValue(englishTranslations, key);
            if (fallback !== undefined && fallback !== null) {
              if (typeof fallback === 'string') {
                return params ? this.interpolateParams(fallback, params) : fallback;
              }
              if (
                typeof fallback === 'object' &&
                !Array.isArray(fallback) &&
                params?.count !== undefined
              ) {
                return this.handlePluralization(
                  fallback,
                  Number(params.count),
                  params,
                  DEFAULT_LANGUAGE
                );
              }
            }
          }
        }

        // Check if defaultValue is provided in params
        if (params?.defaultValue !== undefined) {
          const defaultValue = String(params.defaultValue);
          // Interpolate other params into the default value
          const { defaultValue: _, ...otherParams } = params;
          if (Object.keys(otherParams).length > 0) {
            return this.interpolateParams(defaultValue, otherParams as TranslationParams);
          }
          return defaultValue;
        }

        // Track missing key in development
        if (IS_DEV_MODE && !this.missingKeys.has(key)) {
          console.warn(`[TranslationLoader] Missing translation key: ${key}`, {
            currentLanguage: this.currentLanguage,
            translationsLoaded: Object.keys(this.currentTranslations).length > 0,
            availableTopKeys: Object.keys(this.currentTranslations),
          });
          this.missingKeys.add(key);
        }

        // Return key as fallback
        return key;
      }

      // Handle pluralization with Intl.PluralRules
      if (
        typeof translation === 'object' &&
        !Array.isArray(translation) &&
        params?.count !== undefined
      ) {
        const language = this.getCurrentLanguage();
        return this.handlePluralization(translation, Number(params.count), params, language);
      }

      // Must be a string at this point
      if (typeof translation !== 'string') {
        console.warn(`[TranslationLoader] Invalid translation value for key: ${key}`);
        return key;
      }

      // Interpolate parameters if provided
      if (params) {
        return this.interpolateParams(translation, params);
      }

      return translation;
    } catch (error) {
      console.error(`[TranslationLoader] Error getting translation for key: ${key}`, error);
      return key;
    }
  }

  /**
   * Preload top 20 languages in background for better UX
   */
  async preloadTopLanguages(): Promise<void> {
    try {
      // Mark as preloading to prevent loadTranslation from overwriting current translations
      this.isPreloading = true;

      // Preload in background without blocking
      const preloadPromises = TOP_20_LANGUAGE_CODES.map((lang) =>
        this.loadTranslation(lang).catch(() => {
          // Silently handle preload failures - these are non-critical
        })
      );

      // Wait for all preloads to complete
      await Promise.allSettled(preloadPromises);
    } catch (error) {
      console.error('[TranslationLoader] Error preloading languages:', error);
    } finally {
      // Always reset preloading flag
      this.isPreloading = false;
    }
  }

  /**
   * Clear cache (all languages or specific language)
   * @param language Optional language to clear (clears all if not specified)
   */
  clearCache(language?: string): void {
    if (language) {
      this.cache.delete(language);
    } else {
      this.cache.clear();
    }

    // Also clear missing keys tracking
    if (!language) {
      this.missingKeys.clear();
    }
  }

  /**
   * Get list of cached languages
   * @returns Array of language codes currently in cache
   */
  getCachedLanguages(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * Fetch translation file from server
   * @private
   */
  private async fetchTranslationFile(language: string): Promise<Translations> {
    const url = TRANSLATION_PATH_PATTERN.replace('{lang}', language);

    try {
      const response = await fetch(url);

      if (!response.ok) {
        // Try fallback chain
        if (language !== DEFAULT_LANGUAGE) {
          console.warn(`[TranslationLoader] Language file not found: ${language}, trying fallback`);
          return this.fetchTranslationFile(DEFAULT_LANGUAGE);
        }

        throw new Error(`Failed to fetch translation file: ${response.statusText}`);
      }

      const translations = await response.json();
      return translations;
    } catch (error) {
      console.error(`[TranslationLoader] Error fetching translation file for ${language}:`, error);

      // If this is not the default language, try fallback
      if (language !== DEFAULT_LANGUAGE) {
        return this.fetchTranslationFile(DEFAULT_LANGUAGE);
      }

      // Return empty object as last resort
      return {};
    }
  }

  /**
   * Get value from cache and update access time (LRU)
   * @private
   */
  private getFromCache(language: string): Translations | null {
    const entry = this.cache.get(language);

    if (!entry) {
      return null;
    }

    // Update access time for LRU
    entry.accessTime = Date.now();

    return entry.translations;
  }

  /**
   * Add translations to cache with LRU eviction
   * @private
   */
  private addToCache(language: string, translations: Translations): void {
    // Check if cache is full
    if (this.cache.size >= MAX_CACHE_SIZE && !this.cache.has(language)) {
      // Find least recently used entry
      let lruKey: string | null = null;
      let lruTime = Infinity;

      for (const [key, entry] of this.cache.entries()) {
        if (entry.accessTime < lruTime) {
          lruTime = entry.accessTime;
          lruKey = key;
        }
      }

      // Evict LRU entry
      if (lruKey) {
        this.cache.delete(lruKey);
      }
    }

    // Add to cache
    this.cache.set(language, {
      translations,
      accessTime: Date.now(),
    });
  }

  /**
   * Get nested value from object using dot notation
   * @private
   */
  private getNestedValue(obj: Translations, key: string): string | Translations | undefined {
    const keys = key.split('.');
    let current: Record<string, unknown> | string | undefined = obj;

    for (const k of keys) {
      if (current === null || current === undefined) {
        return undefined;
      }
      if (typeof current === 'string') {
        return undefined;
      }
      current = current[k] as Record<string, unknown> | string | undefined;
    }

    return current as string | Translations | undefined;
  }

  /**
   * Interpolate parameters into translation string
   * Supports both {{key}} (double braces) and {key} (single braces)
   * @private
   */
  private interpolateParams(translation: string, params: TranslationParams): string {
    let result = translation;

    for (const [key, value] of Object.entries(params)) {
      // Replace {{key}} with value (double braces - preferred)
      const doubleBracePattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
      result = result.replace(doubleBracePattern, String(value));

      // Replace {key} with value (single braces - legacy support)
      const singleBracePattern = new RegExp(`\\{${key}\\}`, 'g');
      result = result.replace(singleBracePattern, String(value));
    }

    return result;
  }

  /**
   * Get current language code
   * @private
   */
  private getCurrentLanguage(): string {
    return this.currentLanguage;
  }

  /**
   * Handle pluralization using Intl.PluralRules
   * Supports 6 plural forms: zero, one, two, few, many, other
   * @private
   */
  private handlePluralization(
    pluralForms: Translations,
    count: number,
    params: TranslationParams,
    language: string
  ): string {
    try {
      // Priority fallback chain:
      // 1. 'zero' if count is 0 AND zero form exists (highest priority)
      // 2. 'one' if count is 1 AND one form exists
      // 3. Intl.PluralRules category (two, few, many, etc.)
      // 4. 'other' (required fallback)
      let form: string | undefined;

      // Check for explicit zero form first (overrides PluralRules)
      if (count === 0 && pluralForms.zero) {
        form = pluralForms.zero as string;
      }
      // Check for explicit one form (overrides PluralRules)
      else if (count === 1 && pluralForms.one) {
        form = pluralForms.one as string;
      }
      // Use Intl.PluralRules for other counts
      else {
        const pluralRules = new Intl.PluralRules(language);
        const category = pluralRules.select(count);
        form = pluralForms[category] as string | undefined;
      }

      // Final fallback to 'other' if no match found
      if (!form) {
        form = pluralForms.other as string | undefined;
      }

      // Last resort: use first available form
      if (!form) {
        form = Object.values(pluralForms)[0] as string;
      }

      // Interpolate parameters
      return this.interpolateParams(form, params);
    } catch (error) {
      console.error('[TranslationLoader] Error in pluralization:', error);
      // Fallback to 'other' form on error
      const fallback = (pluralForms.other || Object.values(pluralForms)[0]) as string;
      return this.interpolateParams(fallback, params);
    }
  }
}

// Export singleton instance for convenience (but allow injection for testing)
export const translationLoader = new TranslationLoader();
